import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { toast } from "sonner";
import Logo from "../../components/Logo";
import { Shield } from "lucide-react";

export default function AdminTOTPSetup() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center text-xs font-mono text-[#9A8F83] tracking-widest uppercase">
        // checking clearance…
      </div>
    );
  }
  if (user === false || !user) return <Navigate to="/admin/login" replace />;
  if (user.totp_enabled) return <Navigate to="/admin" replace />;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    api.get("/admin/totp/setup")
      .then((r) => setSetup(r.data))
      .catch(() => navigate("/admin/login"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (e) => {
    e.preventDefault();
    setError("");
    setConfirming(true);
    try {
      await api.post("/admin/totp/setup/confirm", { code });
      setUser((u) => ({ ...u, totp_enabled: true }));
      toast.success("TWO-FACTOR AUTHENTICATION ENABLED");
      navigate("/admin");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid code — try again");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center p-8" data-testid="totp-setup-page">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <Logo />
        </div>

        <p className="eyebrow eyebrow-red flex items-center gap-2">
          <Shield className="w-3 h-3" /> // SECURE SETUP
        </p>
        <h2 className="font-mono text-4xl mt-4 text-white uppercase tracking-tight">
          Enable 2FA
        </h2>
        <p className="mt-4 text-[#9A8F83] text-sm font-sans leading-relaxed">
          Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code to activate.
        </p>

        {!setup ? (
          <p className="mt-10 text-xs font-mono text-[#9A8F83] tracking-widest uppercase">// generating QR code…</p>
        ) : (
          <>
            <div className="mt-8 p-4 bg-white inline-block" data-testid="totp-qr">
              <img src={setup.qr_code} alt="Scan with authenticator app" className="w-48 h-48 block" />
            </div>

            <div className="mt-5 rd-card p-4">
              <p className="text-[10px] font-mono tracking-widest uppercase text-[#9A8F83] mb-1">Manual entry key</p>
              <p className="font-mono text-sm text-white break-all tracking-wider" data-testid="totp-secret">
                {setup.secret}
              </p>
            </div>

            <form onSubmit={confirm} className="mt-8 space-y-6">
              <div>
                <label className="ed-label" htmlFor="totp-code">6-Digit Verification Code</label>
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="ed-input tracking-[0.5em] text-center text-2xl"
                  placeholder="000000"
                  required
                  autoFocus
                  data-testid="totp-confirm-input"
                />
              </div>

              {error && (
                <p className="text-sm text-[#CC1A1A] font-mono tracking-wider uppercase" data-testid="totp-error">
                  // {error}
                </p>
              )}

              <button
                type="submit"
                disabled={confirming || code.length < 6}
                className="btn-rd w-full"
                data-testid="totp-confirm-btn"
              >
                {confirming ? "VERIFYING…" : "ACTIVATE 2FA →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
